import { Clear, Search } from '@mui/icons-material';
import {
  Autocomplete,
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  InputAdornment,
  Popper,
  TextField,
  Theme,
  Tooltip,
} from '@mui/material';
import { useProductsTFunction } from '@retail/products/i18n';
import { MgSupplierItemSearchResult } from '@retail/products/types';
import { forwardRef, useId, useMemo } from 'react';
import { SystemStyleObject } from '@mui/system';
import { SearchResultItem } from './SearchResultItem';

interface Props {
  size?: 'small' | 'medium';
  sx?: SystemStyleObject<Theme>;
  searchResults: MgSupplierItemSearchResult[];
  isLoading: boolean;
  error?: string;
  inputField: {
    name: string;
    value: string;
    onBlur: () => void;
    onChange: (event: any) => void;
  };
  selectOption: (option: MgSupplierItemSearchResult | null) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  externalItemSource: 'FINFO' | 'NOBB';
}

export const SupplierItemAutocomplete = forwardRef(
  (
    {
      sx,
      searchResults,
      inputField,
      isLoading,
      error,
      selectOption,
      label,
      placeholder,
      required,
      size,
      externalItemSource,
    }: Props,
    ref
  ) => {
    const t = useProductsTFunction();
    const inputId = useId();
    const formLabelId = useId();

    const PopperComponent = useMemo(() => {
      if (searchResults.length) {
        return Popper;
      }
      return NullComponent;
    }, [searchResults.length]);

    return (
      <FormControl required={required}>
        <Autocomplete
          size={size}
          sx={(theme) => ({ ...autoCompleteSx(theme), ...sx })}
          renderInput={(params) => {
            const clearValue = () => {
              // @ts-ignore
              params.inputProps.onChange({ target: { value: '' } });
              inputField.onChange('');
              selectOption(null);
            };
            return (
              <>
                <FormLabel htmlFor={inputId} id={formLabelId} sx={{ fontWeight: 600 }}>
                  {label}
                </FormLabel>
                <TextField
                  {...params}
                  {...inputField}
                  id={inputId}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      sx: {
                        paddingRight: '9px !important',
                      },
                      endAdornment: inputField.value ? (
                        <InputAdornment position="end">
                          {isLoading ? (
                            <CircularProgress color="inherit" size={24} />
                          ) : (
                            <Tooltip title={t('products.mgItem.search.reset')}>
                              <IconButton onClick={clearValue}>
                                <Clear />
                              </IconButton>
                            </Tooltip>
                          )}
                        </InputAdornment>
                      ) : undefined,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search className="search-icon" />
                        </InputAdornment>
                      ),
                    },
                  }}
                  error={!!error}
                  placeholder={placeholder}
                  fullWidth
                />
                {error && <FormHelperText error>{error}</FormHelperText>}
              </>
            );
          }}
          options={searchResults}
          // @ts-ignore
          renderOption={({ key, ...props }, option) => (
            <SearchResultItem
              liProps={{
                ...props,
                onClick: (event) => {
                  props.onClick && props.onClick(event);
                  selectOption(option);
                },
              }}
              externalItemSource={externalItemSource}
              key={option.mgSupplierItemId}
              {...option}
            />
          )}
          clearOnBlur={false}
          filterOptions={(option) => option} // must be set, see https://mui.com/material-ui/react-autocomplete/#search-as-you-type
          getOptionLabel={({ primaryText, mgItemNumber }) => primaryText || `${mgItemNumber}`}
          slots={{ popper: PopperComponent }}
          slotProps={{
            listbox: {
              sx: {
                maxHeight: '25rem',
                '& li': {
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': {
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  },
                },
              },
            },
          }}
          noOptionsText={t('products.mgItem.search.noResults')}
        />
      </FormControl>
    );
  }
);

function NullComponent() {
  return null;
}

const autoCompleteSx = (theme: Theme): SystemStyleObject<Theme> => ({
  '&.Mui-focused .search-icon': {
    color: 'primary.main',
  },
  '& .Mui-error .search-icon': {
    color: 'error.main',
  },
  '& .MuiInputBase-root.MuiAutocomplete-inputRoot': {
    paddingY: '4px',
  },
  ...theme.applyStyles('dark', {
    '&.Mui-focused .search-icon': { color: 'primary.light' },
  }),
});
